import { normalizeShortCode } from "../../shared/short-code";
import { apiFetch, cachedRegistrations, clearOffline, deviceState, pendingRedemptions, queueRedemption, remember, scanLocally, syncPending } from "../offline";
import { renderQr } from "../qr";
import type { AdminRegistrationsResponse, RedemptionScanResponse, Registration } from "../types";

const loginMarkup = `
  <main class="admin-login-shell">
    <section class="login-card">
      <a class="brand-link" href="/" aria-label="Wróć do rejestracji">
        <img class="brand-logo" src="/nutrified-logo.png" alt="Nutrified" />
      </a>
      <p class="eyebrow">PANEL ORGANIZATORA</p>
      <h1>Logowanie</h1>
      <p class="intro">Zaloguj się, aby zobaczyć zgłoszenia z Pet Expo 2026.</p>
      <form id="admin-login-form" novalidate>
        <div class="field">
          <label for="admin-password">Hasło administratora</label>
          <input id="admin-password" name="password" type="password" autocomplete="current-password" required autofocus />
        </div>
        <div class="form-error" id="login-error" role="alert" hidden></div>
        <button class="primary-button" type="submit"><span>Zaloguj się</span><span aria-hidden="true">→</span></button>
      </form>
      <a class="back-link" href="/">← Wróć do rejestracji</a>
    </section>
  </main>
`;

const dashboardMarkup = `
  <main class="admin-shell">
    <header class="admin-header">
      <a class="brand-link" href="/">
        <img class="brand-logo" src="/nutrified-logo.png" alt="Nutrified" />
      </a>
      <div class="admin-header-actions">
        <span>Pet Expo 2026</span>
        <button class="text-button" id="logout-button" type="button">Wyloguj</button>
      </div>
    </header>
    <section class="admin-content">
      <div class="admin-title-row">
        <div>
          <p class="eyebrow">PANEL ORGANIZATORA</p>
          <h1>Obsługa nagród</h1>
        </div>
        <div class="admin-stats">
          <div class="stat-card">
            <span class="stat-value" id="registration-count">—</span>
            <span class="stat-label">wszystkich zgłoszeń</span>
          </div>
          <div class="stat-card">
            <span class="stat-value" id="redemption-count">—</span>
            <span class="stat-label">wydanych nagród</span>
          </div>
        </div>
      </div>
      <section class="scanner-card" aria-labelledby="scanner-title">
        <div class="scanner-heading">
          <div>
            <p class="eyebrow">CZYTNIK QR</p>
            <h2 id="scanner-title">Zeskanuj kod uczestnika</h2>
          </div>
          <span class="scanner-ready"><i aria-hidden="true"></i>Czytnik gotowy</span>
        </div>
        <p class="scanner-copy">Kliknij pole raz, a następnie skanuj. Czytnik Bluetooth wpisze kod i zatwierdzi go klawiszem Enter.</p>
        <form class="scanner-form" id="scanner-form">
          <label for="scanner-input">Dane z kodu QR</label>
          <div class="scanner-input-row">
            <input id="scanner-input" name="code" type="text" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" placeholder="Zeskanuj kod QR…" required autofocus />
            <button class="secondary-button" id="scan-button" type="submit">Sprawdź</button>
          </div>
        </form>
        <div class="scanner-result idle" id="scanner-result" role="status" aria-live="polite">
          <span class="scanner-result-mark" aria-hidden="true">⌁</span>
          <div><strong>Oczekiwanie na kod</strong><p>Pole czytnika jest gotowe do skanowania.</p></div>
        </div>
      </section>
      <section class="offline-panel">
        <p id="offline-status" role="status">Przygotowanie panelu offline…</p>
        <p class="scanner-copy">Na iPadzie: Udostępnij → Do ekranu początkowego. Otwórz aplikację z ikony, zaloguj się online i poczekaj na „Gotowy offline”. Używaj jednego iPada do wydawania nagród. Po powrocie sieci pozostaw aplikację otwartą, aż licznik „Do wysłania” spadnie do zera.</p>
        <div class="offline-actions">
          <button class="secondary-button compact" id="sync-button" type="button">Synchronizuj</button>
          <button class="secondary-button compact" id="backup-button" type="button">Pobierz kopię odbiorów</button>
          <button class="text-button" id="reauth-button" type="button">Zaloguj ponownie</button>
        </div>
      </section>
      <div class="toolbar">
        <label class="search-field">
          <span class="sr-only">Szukaj zgłoszenia</span>
          <input id="search-input" type="search" placeholder="Szukaj po imieniu, e-mailu lub telefonie…" />
        </label>
        <button class="secondary-button compact" id="export-button" type="button">Eksport CSV</button>
        <button class="secondary-button compact" id="refresh-button" type="button">Odśwież</button>
        <span class="privacy-note">Lista odświeża się po skanowaniu. Odbiory zapisują się od razu.</span>
      </div>
      <label class="table-privacy"><input id="hide-names" type="checkbox" /> Ukryj dane uczestników w tabeli</label>
      <div class="table-card">
        <div class="loading-state" id="admin-loading">Pobieramy zgłoszenia…</div>
        <div class="form-error inset" id="admin-error" role="alert" hidden></div>
        <div class="table-scroll" id="table-wrap" hidden>
          <table>
            <thead><tr><th>Osoba</th><th>Telefon</th><th>Nagroda</th><th>Marketing</th><th>Data</th><th>QR</th></tr></thead>
            <tbody id="registration-rows"></tbody>
          </table>
          <div class="empty-state" id="empty-state" hidden>Brak pasujących zgłoszeń.</div>
        </div>
      </div>
    </section>
  </main>
  <dialog id="qr-dialog">
    <button class="dialog-close" type="button" aria-label="Zamknij">×</button>
    <p class="eyebrow">KOD REJESTRACJI</p>
    <h2 id="dialog-name"></h2>
    <canvas id="dialog-qr" aria-label="Kod QR zgłoszenia"></canvas>
    <p class="dialog-meta" id="dialog-meta"></p>
  </dialog>
`;

let registrations: Registration[] = [];
let hideNames = false;
try { hideNames = localStorage.getItem("petexpo26-hide-names") === "true"; } catch { /* Storage may be disabled. */ }

function escapeHtml(value: string) {
  return value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
  })[character]!);
}

function formatDate(value: string) {
  if (!value || !Number.isFinite(Date.parse(value))) return "—";
  return new Intl.DateTimeFormat("pl-PL", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function toCsv(records: Registration[]) {
  const cells = (values: unknown[]) => values.map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`).join(",");
  return [
    cells(["ID", "Imię i nazwisko", "E-mail", "Telefon", "Nagroda odebrana", "Data odbioru", "Zgoda marketingowa", "Data zgody", "Data rejestracji"]),
    ...records.map((item) => cells([
      item.id, item.name, item.email, item.phone, item.redemption ? "tak" : "nie", item.redemption?.redeemedAt ?? "",
      item.marketingConsent === undefined ? "" : item.marketingConsent ? "tak" : "nie",
      item.marketingConsentAt ?? "", item.createdAt,
    ])),
  ].join("\r\n");
}

function renderRows(root: HTMLElement, filter = "") {
  const normalized = filter.trim().toLocaleLowerCase("pl");
  const visible = registrations.filter((item) => [item.name, item.email, item.phone].some((value) => (value ?? "").toLocaleLowerCase("pl").includes(normalized)));
  const rows = root.querySelector<HTMLTableSectionElement>("#registration-rows")!;
  const empty = root.querySelector<HTMLElement>("#empty-state")!;
  rows.innerHTML = visible.map((item) => `
    <tr>
      <td><strong>${hideNames ? "Uczestnik · " + escapeHtml(item.id.slice(-6)) : escapeHtml(item.name)}</strong><span>${hideNames ? "" : escapeHtml(item.email ?? "")}</span></td>
      <td>${hideNames ? "—" : escapeHtml(item.phone ?? "—")}</td>
      <td><span class="status ${item.redemption ? "redeemed" : "pending"}">${item.redemption ? "Odebrana" : "Do wydania"}</span></td>
      <td><span class="status ${item.marketingConsent ? "yes" : "no"}">${item.marketingConsent === undefined ? "—" : item.marketingConsent ? "Tak" : "Nie"}</span></td>
      <td>${escapeHtml(formatDate(item.createdAt))}</td>
      <td><button class="qr-button" type="button" data-registration-id="${escapeHtml(item.id)}">Pokaż QR</button></td>
    </tr>
  `).join("");
  empty.hidden = visible.length > 0;
}

async function loadDashboard(root: HTMLElement, fresh = false) {
  const response = await apiFetch(`/api/admin/registrations${fresh ? "?refresh=1" : ""}`, { headers: { Accept: "application/json" } });
  if (response.status === 401) {
    if ((await deviceState()).enabled) throw new Error("Sesja wygasła. Zaloguj się ponownie, aby synchronizować. Skaner offline nadal działa.");
    renderLogin(root);
    return;
  }
  const payload = await response.json() as AdminRegistrationsResponse & { error?: string };
  if (!response.ok) throw new Error(payload.error || "Nie udało się pobrać zgłoszeń.");
  await remember(payload.registrations, true);
  const cached = new Map((await cachedRegistrations()).map(record => [record.id, record]));
  registrations = payload.registrations.map(record => ({ ...record, redemption: cached.get(record.id)?.redemption ?? record.redemption }));
  for (const record of cached.values()) if (!registrations.some(item => item.id === record.id)) registrations.push(record);
  paintDashboard(root);
  await updateOfflineStatus(root);
}

function paintDashboard(root: HTMLElement) {
  if (!root.querySelector("#registration-count")) return;
  root.querySelector<HTMLElement>("#registration-count")!.textContent = String(registrations.length);
  root.querySelector<HTMLElement>("#redemption-count")!.textContent = String(registrations.filter(item => item.redemption).length);
  renderRows(root, root.querySelector<HTMLInputElement>("#search-input")!.value);
  root.querySelector<HTMLElement>("#admin-loading")!.hidden = true;
  root.querySelector<HTMLElement>("#table-wrap")!.hidden = false;
}

function updateRegistrationRedemption(root: HTMLElement, payload: RedemptionScanResponse) {
  if (!payload.registration || !payload.redemption) return;
  const index = registrations.findIndex((item) => item.id === payload.registration!.id);
  if (index >= 0) registrations[index] = { ...registrations[index], redemption: payload.redemption };
  else registrations.unshift({ ...payload.registration, redemption: payload.redemption });
  paintDashboard(root);
}

function showScannerResult(root: HTMLElement, payload: RedemptionScanResponse, justRedeemed = false) {
  const result = root.querySelector<HTMLElement>("#scanner-result")!;
  if (payload.outcome === "ready" && payload.registration) {
    result.className = "scanner-result valid";
    result.innerHTML = `
      <span class="scanner-result-mark" aria-hidden="true">TAK</span>
      <div class="scanner-result-copy"><strong>${payload.provisional ? "Kod przyjęty warunkowo" : "Kod ważny"}</strong><p>${escapeHtml(payload.registration.name)} · ${payload.provisional ? "Dane zostaną sprawdzone podczas synchronizacji" : "Rejestracja w pamięci iPada"}</p></div>
      <button class="redeem-button" id="redeem-button" type="button">Wydaj nagrodę</button>
    `;
    return;
  }
  if (justRedeemed && payload.registration) {
    result.className = "scanner-result success";
    result.innerHTML = `
      <span class="scanner-result-mark" aria-hidden="true">✓</span>
      <div class="scanner-result-copy"><strong>Nagroda wydana</strong><p>${escapeHtml(payload.registration.name)} · zapisano na iPadzie ${escapeHtml(formatDate(payload.redemption!.redeemedAt))}</p></div>
    `;
    return;
  }
  if (payload.outcome === "redeemed" && payload.registration) {
    const redeemedAt = payload.redemption ? formatDate(payload.redemption.redeemedAt) : "wcześniej";
    result.className = "scanner-result rejected";
    result.innerHTML = `
      <span class="scanner-result-mark" aria-hidden="true">NIE</span>
      <div class="scanner-result-copy"><strong>Nagroda już odebrana</strong><p>${escapeHtml(payload.registration.name)} · ${escapeHtml(redeemedAt)}</p></div>
    `;
    return;
  }
  result.className = "scanner-result rejected";
  result.innerHTML = `
    <span class="scanner-result-mark" aria-hidden="true">NIE</span>
    <div class="scanner-result-copy"><strong>Kod nieprawidłowy</strong><p>${escapeHtml(payload.error ?? "Nie znaleziono zgłoszenia.")}</p></div>
  `;
}

function bindScanner(root: HTMLElement) {
  const form = root.querySelector<HTMLFormElement>("#scanner-form")!;
  const input = root.querySelector<HTMLInputElement>("#scanner-input")!;
  const scanButton = root.querySelector<HTMLButtonElement>("#scan-button")!;
  let scannedCode = "";
  let legacy: Registration | undefined;
  let busy = false;
  let scanRevision = 0;
  requestAnimationFrame(() => input.focus({ preventScroll: true }));
  input.addEventListener("input", () => {
    scanRevision++;
    scannedCode = "";
    legacy = undefined;
    root.querySelector<HTMLButtonElement>("#redeem-button")?.remove();
  });
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const code = input.value.trim();
    if (!code || busy) return;
    busy = true;
    const revision = ++scanRevision;
    scannedCode = "";
    legacy = undefined;
    input.readOnly = scanButton.disabled = true;
    root.querySelector<HTMLButtonElement>("#redeem-button")?.remove();
    try {
      let payload: RedemptionScanResponse;
      if (normalizeShortCode(code)) payload = await scanLocally(code);
      else {
        if (!navigator.onLine) throw new Error("Starszy QR nie ma podpisu offline. Połącz się z internetem lub pokaż nowy kod z panelu.");
        const response = await apiFetch("/api/admin/redemptions/scan", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code }) });
        if (response.status === 401) throw new Error("Zaloguj się ponownie, aby sprawdzić starszy QR online.");
        payload = await response.json() as RedemptionScanResponse;
        if (!response.ok) throw new Error(payload.error || "Nie udało się sprawdzić kodu.");
        if (payload.registration) {
          await remember([{ ...payload.registration, redemption: payload.redemption }]);
          // Respect handouts still waiting in the local outbox.
          const local = (await cachedRegistrations()).find(record => record.id === payload.registration!.id);
          if (local?.redemption) payload = { outcome: "redeemed", registration: local, redemption: local.redemption };
          else legacy = payload.registration;
        }
      }
      scannedCode = code;
      showScannerResult(root, payload);
      // Show the local result immediately, then fetch a fresh table for this scan.
      // Never let a slow response replace a newer scan or a completed handout.
      if (navigator.onLine && normalizeShortCode(code)) {
        void loadDashboard(root, true).then(async () => {
          if (scanRevision !== revision || scannedCode !== code) return;
          const refreshed = await scanLocally(code);
          if (scanRevision === revision && scannedCode === code) showScannerResult(root, refreshed);
        }).catch(error => updateOfflineStatus(root, error instanceof Error ? error.message : "Brak połączenia."));
      }
    } catch (caught) {
      showScannerResult(root, { outcome: "invalid", error: caught instanceof Error ? caught.message : "Nie udało się sprawdzić kodu." });
    } finally {
      busy = false;
      input.readOnly = scanButton.disabled = false;
      input.focus({ preventScroll: true });
      input.select();
    }
  });
  root.querySelector<HTMLElement>("#scanner-result")!.addEventListener("click", async event => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("#redeem-button");
    if (!button || !scannedCode || busy) return;
    busy = true;
    scanRevision++;
    button.disabled = input.readOnly = scanButton.disabled = true;
    try {
      const payload = await queueRedemption(scannedCode, legacy);
      updateRegistrationRedemption(root, payload);
      showScannerResult(root, payload, !payload.alreadyRedeemed);
      await updateOfflineStatus(root);
      void synchronize(root);
    } catch (caught) {
      showScannerResult(root, { outcome: "invalid", error: caught instanceof Error ? caught.message : "Nie udało się zapisać odbioru. Nie wydawaj nagrody." });
    } finally {
      busy = false;
      input.readOnly = scanButton.disabled = false;
      input.value = scannedCode = "";
      legacy = undefined;
      input.focus({ preventScroll: true });
    }
  });
}

function bindDashboard(root: HTMLElement) {
  const dialog = root.querySelector<HTMLDialogElement>("#qr-dialog")!;
  bindScanner(root);
  const privacy = root.querySelector<HTMLInputElement>("#hide-names")!;
  privacy.checked = hideNames;
  privacy.addEventListener("change", () => {
    hideNames = privacy.checked;
    try { localStorage.setItem("petexpo26-hide-names", String(hideNames)); } catch { /* Keep the current view private even if persistence is unavailable. */ }
    renderRows(root, root.querySelector<HTMLInputElement>("#search-input")!.value);
  });
  root.querySelector("#sync-button")!.addEventListener("click", () => void synchronize(root, true));
  root.querySelector("#reauth-button")!.addEventListener("click", () => renderLogin(root));
  root.querySelector("#backup-button")!.addEventListener("click", () => {
    void pendingRedemptions().then(pending => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), pending }, null, 2)], { type: "application/json" }));
      link.download = `petexpo26-odbiory-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    }).catch(error => showAdminError(root, error));
  });
  root.querySelector<HTMLInputElement>("#search-input")!.addEventListener("input", (event) => renderRows(root, (event.target as HTMLInputElement).value));
  root.querySelector<HTMLButtonElement>("#refresh-button")!.addEventListener("click", () => void loadDashboard(root, true).catch(showAdminError.bind(null, root)));
  root.querySelector<HTMLButtonElement>("#logout-button")!.addEventListener("click", async () => {
    try {
      if ((await pendingRedemptions()).length) throw new Error("Nie można wylogować: odbiory czekają na synchronizację lub wyjaśnienie. Dane pozostają na iPadzie.");
      const response = await apiFetch("/api/admin/logout", { method: "POST" });
      if (!response.ok) throw new Error("Nie udało się wylogować.");
      await clearOffline();
      renderLogin(root);
    } catch (error) { showAdminError(root, error); }
  });
  root.querySelector<HTMLButtonElement>("#export-button")!.addEventListener("click", () => {
    const blob = new Blob(["\ufeff", toCsv(registrations)], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `petexpo26-rejestracje-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  });
  root.querySelector<HTMLElement>("#registration-rows")!.addEventListener("click", async (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-registration-id]");
    if (!button) return;
    const registration = registrations.find((item) => item.id === button.dataset.registrationId);
    if (!registration) return;
    root.querySelector<HTMLElement>("#dialog-name")!.textContent = registration.name;
    root.querySelector<HTMLElement>("#dialog-meta")!.textContent = [registration.email, registration.phone].filter(Boolean).join(" · ");
    await renderQr(root.querySelector<HTMLCanvasElement>("#dialog-qr")!, registration);
    dialog.showModal();
  });
  root.querySelector<HTMLButtonElement>(".dialog-close")!.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
}

function showAdminError(root: HTMLElement, caught: unknown) {
  const loading = root.querySelector<HTMLElement>("#admin-loading");
  if (!loading) return;
  loading.hidden = true;
  const error = root.querySelector<HTMLElement>("#admin-error")!;
  error.textContent = caught instanceof Error ? caught.message : "Wystąpił błąd.";
  error.hidden = false;
}

let cleanupDashboard = () => {};
async function updateOfflineStatus(root: HTMLElement, message = "") {
  const element = root.querySelector<HTMLElement>("#offline-status");
  if (!element) return;
  const [state, pending] = await Promise.all([deviceState(), pendingRedemptions()]);
  const ready = state.enabled && !!navigator.serviceWorker?.controller;
  const conflicts = pending.filter(item => item.conflict);
  element.textContent = `${ready ? "Gotowy offline" : "Przygotowanie offline — pozostań online"} · ${navigator.onLine ? "Sieć dostępna" : "Brak sieci"} · Do wysłania: ${pending.length - conflicts.length} · Konflikty: ${conflicts.length}${state.lastSync ? " · Ostatnia synchronizacja: " + formatDate(state.lastSync) : ""}. ${message || conflicts.map(item => item.conflict).join(" ")}`;
}
// Idle ticks upload queued handouts only. Scans and explicit refreshes fetch the table.
let refreshBusy = false;
async function synchronize(root: HTMLElement, refresh = false) {
  if (refreshBusy) return;
  refreshBusy = true;
  try {
    await syncPending();
    if (!root.querySelector("#offline-status")) return;
    if (refresh && navigator.onLine) await loadDashboard(root, true);
    else {
      const cached = await cachedRegistrations();
      registrations = cached.map(record => ({ ...registrations.find(item => item.id === record.id), ...record }));
      paintDashboard(root);
    }
    await updateOfflineStatus(root);
  } catch (error) {
    await updateOfflineStatus(root, error instanceof Error ? error.message : "Brak połączenia. Odbiory pozostają na iPadzie.");
  } finally { refreshBusy = false; }
}
function renderDashboard(root: HTMLElement) {
  cleanupDashboard();
  root.innerHTML = dashboardMarkup;
  registrations = [];
  bindDashboard(root);
  void (async () => {
    registrations = await cachedRegistrations();
    paintDashboard(root);
    await updateOfflineStatus(root);
    try { await loadDashboard(root, true); }
    catch (error) { await updateOfflineStatus(root, error instanceof Error ? error.message : "Brak połączenia."); }
    await synchronize(root);
  })().catch(error => showAdminError(root, error));
  const refresh = () => { if (!document.hidden) void synchronize(root); };
  const status = () => { void updateOfflineStatus(root); };
  window.addEventListener("online", refresh);
  window.addEventListener("offline", status);
  document.addEventListener("visibilitychange", refresh);
  navigator.serviceWorker?.addEventListener("controllerchange", status);
  const timer = window.setInterval(refresh, 15_000);
  cleanupDashboard = () => {
    clearInterval(timer);
    window.removeEventListener("online", refresh);
    window.removeEventListener("offline", status);
    document.removeEventListener("visibilitychange", refresh);
    navigator.serviceWorker?.removeEventListener("controllerchange", status);
  };
}

function renderLogin(root: HTMLElement) {
  cleanupDashboard();
  root.innerHTML = loginMarkup;
  const form = root.querySelector<HTMLFormElement>("#admin-login-form")!;
  const error = root.querySelector<HTMLElement>("#login-error")!;
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    const submit = form.querySelector<HTMLButtonElement>("button[type='submit']")!;
    submit.disabled = true;
    error.hidden = true;
    try {
      const response = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: new FormData(form).get("password") }),
      });
      const payload = await response.json() as { error?: string };
      if (!response.ok) throw new Error(payload.error || "Nie udało się zalogować.");
      renderDashboard(root);
    } catch (caught) {
      error.textContent = caught instanceof Error ? caught.message : "Nie udało się zalogować.";
      error.hidden = false;
    } finally {
      submit.disabled = false;
    }
  });
}

export async function renderAdmin(root: HTMLElement) {
  document.title = "Panel organizatora · Nutrified Pet Expo 2026";
  const robots = document.createElement("meta");
  robots.name = "robots";
  robots.content = "noindex, nofollow, noarchive";
  document.head.appendChild(robots);
  root.innerHTML = '<main class="admin-login-shell"><div class="loading-state">Sprawdzamy sesję…</div></main>';
  try {
    const response = await apiFetch("/api/admin/session", { headers: { Accept: "application/json" } });
    const payload = await response.json() as { authenticated?: boolean };
    if (response.ok && (payload.authenticated || (await deviceState()).enabled)) renderDashboard(root);
    else renderLogin(root);
  } catch {
    try {
      if ((await deviceState()).enabled) renderDashboard(root);
      else renderLogin(root);
    } catch { renderLogin(root); }
  }
}
